/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import type { FlexDateFilters } from 'shared-ui/flex-date-picker';

import type { FiltersProps } from './FlexDates.types';

/** Legacy flex fields are typed as strings, but Redux can hold numbers. */
type LegacyRangeValue = string | number | null | undefined;

const MONTH_YEAR_PATTERN = /^(\d{2})-(\d{4})$/;
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-\d{2}$/;

/**
 * Converts a legacy `MM-YYYY` month into the ISO date FlexDatePicker stores.
 * Flexible months are always the first day of that month.
 */
function monthYearToIso(monthYear: string): string | null {
    const match = MONTH_YEAR_PATTERN.exec(monthYear);
    if (!match) {
        return null;
    }

    const [, month, year] = match;
    return `${year}-${month}-01`;
}

/**
 * Converts a FlexDatePicker ISO month (`YYYY-MM-DD`) back to legacy `MM-YYYY`.
 */
function isoToMonthYear(isoDate: string): string | null {
    const match = ISO_DATE_PATTERN.exec(isoDate);
    if (!match) {
        return null;
    }

    const [, year, month] = match;
    return `${month}-${year}`;
}

/**
 * Parses a legacy range or duration into a number.
 * Empty values stay null so the new picker treats them as unset.
 */
function toOptionalNumber(value: LegacyRangeValue): number | null {
    if (value == null || value === '') {
        return null;
    }

    const parsed = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(parsed)) {
        return null;
    }

    return parsed;
}

/**
 * Normalizes legacy range strings and numbers so unchanged filters compare equal.
 */
function toComparableString(value: LegacyRangeValue): string | null {
    if (value == null || value === '') {
        return null;
    }

    return String(value);
}

/**
 * Maps legacy FlexDates filters into FlexDatePicker's value.
 */
export function toFlexDateFilters(legacy: FiltersProps): FlexDateFilters {
    const availMonths = (legacy.months ?? [])
        .map((month) => monthYearToIso(month))
        .filter((month): month is string => month !== null);

    return {
        startDate: legacy.startDate ?? null,
        endDate: legacy.endDate ?? null,
        availMonths: availMonths.length > 0 ? availMonths : null,
        availNights: toOptionalNumber(legacy.duration),
        flexStartRange: toOptionalNumber(legacy.startRange),
        flexEndRange: toOptionalNumber(legacy.endRange),
    };
}

/**
 * Maps a FlexDatePicker value back to the legacy filter object.
 * Null fields are omitted so a full clear becomes `{}`, which the existing
 * handlers treat as clearing every date field.
 */
export function toLegacyFilters(value: FlexDateFilters): FiltersProps {
    const filters: FiltersProps = {};

    if (value.startDate) {
        filters.startDate = value.startDate;
    }
    if (value.endDate) {
        filters.endDate = value.endDate;
    }

    const months = (value.availMonths ?? [])
        .map((month) => isoToMonthYear(month))
        .filter((month): month is string => month !== null);
    if (months.length > 0) {
        filters.months = months;
    }

    if (value.availNights != null) {
        filters.duration = String(value.availNights);
    }
    if (value.flexStartRange != null) {
        filters.startRange = String(value.flexStartRange);
    }
    if (value.flexEndRange != null) {
        filters.endRange = String(value.flexEndRange);
    }

    return filters;
}

/**
 * Reports whether two legacy filter objects would write the same search criteria.
 */
export function legacyFiltersEqual(left: FiltersProps, right: FiltersProps): boolean {
    const leftStart = left.startDate?.toString() ?? null;
    const rightStart = right.startDate?.toString() ?? null;
    const leftEnd = left.endDate?.toString() ?? null;
    const rightEnd = right.endDate?.toString() ?? null;

    if (leftStart !== rightStart || leftEnd !== rightEnd) {
        return false;
    }

    if (toComparableString(left.startRange) !== toComparableString(right.startRange)) {
        return false;
    }
    if (toComparableString(left.endRange) !== toComparableString(right.endRange)) {
        return false;
    }
    if (toComparableString(left.duration) !== toComparableString(right.duration)) {
        return false;
    }

    const leftMonths = left.months ?? [];
    const rightMonths = right.months ?? [];
    if (leftMonths.length !== rightMonths.length) {
        return false;
    }

    return leftMonths.every((month, index) => month === rightMonths[index]);
}
